// The Purchasing tab body (F-FE-N97): an action-first queue, not a status
// table. Tiles say what's on the way and what's blocked; each row carries
// exactly ONE primary action (the order's next lifecycle step) and opens a
// detail sheet for everything else. Rows are flex cards, not a min-width
// table — this is also the mobile fix for the tab's known breakage.
import { useMemo, useState } from 'react'
import { Search, Plus, ChevronRight, PackageOpen } from 'lucide-react'
import type { PurchaseOrderSummary, Supplier, StockAlert } from '@/types'
import { useCurrency } from '@/contexts/CurrencyContext'
import { compactCurrency } from '@/components/admin/dashboard/dashboardFormat'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import type { VendorTone } from './vendorStatus'
import {
  poRef,
  poStatusLabel,
  poStatusTone,
  poBucket,
  poPrimaryAction,
  PO_PRIMARY_ACTION_LABEL,
  poExpectedInfo,
  poQueueCompare,
} from './poStatus'
import { PURCHASING_STRINGS } from './purchasing/purchasingStrings'

type Chip = 'active' | 'arriving' | 'approval' | 'ready' | 'history'

const TONE_CLASS: Record<VendorTone, string> = {
  danger: 'bg-danger-surface text-danger-fg border-danger-border',
  warning: 'bg-warning-surface text-warning-fg border-warning-border',
  success: 'bg-success-surface text-success-fg border-success-border',
  info: 'bg-info-surface text-info-fg border-info-border',
  neutral: 'bg-neutral-surface text-neutral-fg border-neutral-border',
}

const TONE_TEXT: Record<VendorTone, string> = {
  danger: 'text-danger-fg font-medium',
  warning: 'text-warning-fg',
  success: 'text-success-fg',
  info: 'text-info-fg',
  neutral: 'text-muted-foreground',
}

function StatusPill({ tone, text }: { tone: VendorTone; text: string }) {
  return (
    <span className={cn('inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium whitespace-nowrap', TONE_CLASS[tone])}>
      <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden="true" />
      {text}
    </span>
  )
}

function Tile({ label, value, sub, alarm, onClick }: { label: string; value: string; sub?: string; alarm?: boolean; onClick?: () => void }) {
  const body = (
    <CardContent className="flex flex-col gap-1 px-4 py-3.5">
      <span className={cn('text-[11px] font-medium uppercase tracking-wide', alarm ? 'text-danger-fg/85' : 'text-muted-foreground')}>
        {label}
      </span>
      <span className={cn('text-2xl font-semibold tabular-nums', alarm && 'text-danger-fg')}>{value}</span>
      {sub && <span className={cn('text-xs', alarm ? 'text-danger-fg/90' : 'text-muted-foreground')}>{sub}</span>}
    </CardContent>
  )
  if (onClick) {
    return (
      <Card
        role="button"
        tabIndex={0}
        className={cn('cursor-pointer transition-colors hover:border-primary/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring', alarm && 'border-danger-border bg-danger-surface')}
        onClick={onClick}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick() } }}
      >
        {body}
      </Card>
    )
  }
  return <Card className={cn(alarm && 'border-danger-border bg-danger-surface')}>{body}</Card>
}

export function PurchasingQueue({
  orders,
  suppliers,
  alerts,
  isAdmin,
  canApprove,
  canApproveOrder,
  loading,
  loadError = false,
  onRetry,
  hasMore,
  onLoadMore,
  onOpenPo,
  onNewOrder,
  onOrderLow,
  onPrimaryAction,
  newLabel = 'New order',
  newNeedsVendor = true,
}: {
  orders: PurchaseOrderSummary[]
  suppliers: Supplier[]
  alerts: StockAlert[]
  isAdmin: boolean
  /** Kitchen flow lane D: may this user approve? Defaults to isAdmin. */
  canApprove?: boolean
  /** Kitchen flow pilot (Batch 5): may this user approve THIS order? (Off
   *  self-approval hides Approve on your own.) Defaults to canApprove. */
  canApproveOrder?: (o: PurchaseOrderSummary) => boolean
  loading: boolean
  /** The orders failed to load: say so with Retry instead of "No orders yet". */
  loadError?: boolean
  onRetry?: () => void
  hasMore: boolean
  onLoadMore: () => void
  onOpenPo: (id: string) => void
  onNewOrder: () => void
  onOrderLow: () => void
  onPrimaryAction: (o: PurchaseOrderSummary, action: 'approve' | 'send' | 'receive') => void
  /** F-CTRL-N75: "New purchase" when stock can also be recorded as already
   *  bought — which needs no vendor on file (a market stall will do). */
  newLabel?: string
  newNeedsVendor?: boolean
}) {
  const { formatCurrency } = useCurrency()
  const today = new Date()

  const [query, setQuery] = useState('')
  const [chip, setChip] = useState<Chip>('active')
  const [vendorFilter, setVendorFilter] = useState('')

  const tiles = useMemo(() => {
    const arriving = orders.filter((o) => poBucket(o.status) === 'arriving')
    const onOrderValue = arriving.reduce((s, o) => s + (o.ordered_value ?? 0), 0)
    const arrivingToday = arriving.filter((o) => poExpectedInfo(o, today).daysUntil === 0)
    const todayVendors = [...new Set(arrivingToday.map((o) => o.supplier_name))]
    const approval = orders.filter((o) => poBucket(o.status) === 'needs_approval')
    const approvalValue = approval.reduce((s, o) => s + (o.ordered_value ?? 0), 0)
    const low = alerts.filter((a) => a.suggested_qty > 0).length
    return { arriving, onOrderValue, arrivingToday, todayVendors, approval, approvalValue, low }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orders, alerts])

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase()
    return orders
      .filter((o) => {
        const bucket = poBucket(o.status)
        if (chip === 'active' && bucket === 'history') return false
        if (chip === 'arriving' && bucket !== 'arriving') return false
        if (chip === 'approval' && bucket !== 'needs_approval') return false
        if (chip === 'ready' && bucket !== 'ready_to_send') return false
        if (chip === 'history' && bucket !== 'history') return false
        if (vendorFilter && o.supplier_id !== vendorFilter) return false
        if (q) {
          const hay = `${o.supplier_name} ${poRef(o.id)} ${o.notes ?? ''}`.toLowerCase()
          if (!hay.includes(q)) return false
        }
        return true
      })
      .sort((a, b) => poQueueCompare(a, b, today))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orders, query, chip, vendorFilter])

  return (
    <div className="space-y-5 pb-12">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Tile
          label="On order"
          value={tiles.onOrderValue > 0 ? compactCurrency(tiles.onOrderValue) : tiles.arriving.length > 0 ? String(tiles.arriving.length) : '—'}
          sub={tiles.arriving.length > 0 ? `${tiles.arriving.length} order${tiles.arriving.length === 1 ? '' : 's'} on the way` : 'nothing on the way'}
        />
        <Tile
          label="Arriving today"
          value={tiles.arrivingToday.length > 0 ? String(tiles.arrivingToday.length) : '—'}
          sub={
            tiles.arrivingToday.length > 0
              ? tiles.todayVendors.slice(0, 2).join(', ') + (tiles.todayVendors.length > 2 ? '…' : '')
              : 'nothing due today'
          }
        />
        <Tile
          label="Waiting for approval"
          value={tiles.approval.length > 0 ? String(tiles.approval.length) : '—'}
          sub={
            tiles.approval.length > 0
              ? tiles.approvalValue > 0
                ? `${compactCurrency(tiles.approvalValue)} needs an admin's OK`
                : "needs an admin's OK"
              : 'nothing waiting'
          }
          alarm={(canApprove ?? isAdmin) && tiles.approval.length > 0}
          onClick={tiles.approval.length > 0 ? () => setChip('approval') : undefined}
        />
        <Tile
          label="Running low"
          value={tiles.low > 0 ? String(tiles.low) : '—'}
          sub={tiles.low > 0 ? 'tap to start an order' : 'stock levels healthy'}
          onClick={tiles.low > 0 ? onOrderLow : undefined}
        />
      </div>

      <Card>
        <div className="flex flex-wrap items-center gap-2.5 px-4 py-3.5">
          <div className="relative w-full max-w-xs flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="search"
              className="h-10 pl-9"
              placeholder="Search orders…"
              aria-label="Search orders"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <select
            className="h-10 rounded-md border border-input bg-background px-2.5 text-sm"
            aria-label="Filter by vendor"
            value={vendorFilter}
            onChange={(e) => setVendorFilter(e.target.value)}
          >
            <option value="">All vendors</option>
            {suppliers.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
          <div className="flex items-center gap-1 rounded-md bg-muted p-1" role="group" aria-label="Filter orders">
            {([
              ['active', 'All active'],
              ['arriving', 'Arriving'],
              ['approval', 'Needs approval'],
              ['ready', 'Ready to send'],
              ['history', 'History'],
            ] as [Chip, string][]).map(([id, label]) => (
              <Button
                key={id}
                size="sm"
                variant={chip === id ? 'default' : 'ghost'}
                className="h-7 px-3 text-xs"
                aria-pressed={chip === id}
                onClick={() => setChip(id)}
              >
                {label}
              </Button>
            ))}
          </div>
          <div className="ml-auto">
            <Button
              size="sm"
              className="h-10"
              disabled={newNeedsVendor && suppliers.length === 0}
              title={newNeedsVendor && suppliers.length === 0 ? 'Add a vendor on the Vendors tab first' : undefined}
              onClick={onNewOrder}
            >
              <Plus className="mr-1 h-4 w-4" /> {newLabel}
            </Button>
          </div>
        </div>

        <div className="border-t">
          {loading ? (
            <div className="px-6 py-10 text-center text-sm text-muted-foreground">Loading orders…</div>
          ) : loadError && orders.length === 0 ? (
            <div role="alert" className="px-6 py-10 text-center">
              <p className="text-sm font-medium text-danger-fg">{PURCHASING_STRINGS.errors.orders}</p>
              {onRetry && (
                <Button variant="outline" size="sm" className="mt-3" onClick={onRetry}>{PURCHASING_STRINGS.errors.retry}</Button>
              )}
            </div>
          ) : orders.length === 0 ? (
            <div className="px-6 py-12 text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
                <PackageOpen className="h-6 w-6" />
              </div>
              <p className="mt-3 text-sm font-medium">No orders yet</p>
              <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
                {suppliers.length === 0
                  ? 'Add a vendor on the Vendors tab, then start your first order here.'
                  : 'Orders you send to vendors — and the deliveries against them — live here.'}
              </p>
              {suppliers.length > 0 && (
                <Button className="mt-4" onClick={onNewOrder}><Plus className="mr-1 h-4 w-4" /> Start an order</Button>
              )}
            </div>
          ) : rows.length === 0 ? (
            <div className="px-6 py-10 text-center">
              <p className="text-sm font-medium">No orders match</p>
              <p className="mt-1 text-sm text-muted-foreground">Try a different search, or clear the filters.</p>
              <Button variant="outline" size="sm" className="mt-3" onClick={() => { setQuery(''); setChip('active'); setVendorFilter('') }}>
                Clear filters
              </Button>
            </div>
          ) : (
            <ul className="divide-y">
              {rows.map((o) => {
                const info = poExpectedInfo(o, today)
                const action = poPrimaryAction(o.status, canApproveOrder ? canApproveOrder(o) : (canApprove ?? isAdmin))
                const done = poBucket(o.status) === 'history'
                const money = done ? (o.received_value ?? 0) : (o.ordered_value ?? 0)
                return (
                  <li key={o.id}>
                    <div
                      role="button"
                      tabIndex={0}
                      aria-label={`Open order for ${o.supplier_name}`}
                      className="group flex cursor-pointer flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3.5 hover:bg-muted/50 focus-visible:bg-muted/50 focus-visible:outline-none"
                      onClick={() => onOpenPo(o.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpenPo(o.id) }
                      }}
                    >
                      <div className="min-w-0 flex-1 basis-52">
                        <p className="flex flex-wrap items-baseline gap-x-2 font-medium">
                          <span className="truncate">{o.supplier_name}</span>
                          <span className="text-xs font-normal text-muted-foreground tabular-nums">#{poRef(o.id)}</span>
                          <span className="text-xs font-normal text-muted-foreground">
                            {(o.line_count ?? 0) > 0 ? `${o.line_count} item${o.line_count === 1 ? '' : 's'}` : ''}
                          </span>
                        </p>
                        <p className={cn('mt-0.5 text-sm', TONE_TEXT[info.tone])}>
                          {info.text}
                          {money > 0 && (
                            <span className="text-muted-foreground font-normal"> · {formatCurrency(money)}</span>
                          )}
                          {done && o.bill_status && (
                            <span className={cn('font-normal', (o.bill_outstanding ?? 0) > 0.01 ? 'text-warning-fg' : 'text-muted-foreground')}>
                              {' · '}
                              {(o.bill_outstanding ?? 0) > 0.01
                                ? `${formatCurrency(o.bill_outstanding ?? 0)} owed`
                                : o.bill_status === 'draft'
                                  ? 'bill to confirm'
                                  : 'bill paid'}
                            </span>
                          )}
                        </p>
                      </div>
                      <div className="flex shrink-0 items-center gap-2.5">
                        <StatusPill tone={poStatusTone(o.status)} text={poStatusLabel(o.status)} />
                        {action && (
                          <Button
                            size="sm"
                            variant={action === 'receive' ? 'default' : 'secondary'}
                            className="h-8 text-xs"
                            onClick={(e) => { e.stopPropagation(); onPrimaryAction(o, action) }}
                          >
                            {PO_PRIMARY_ACTION_LABEL[action]}
                          </Button>
                        )}
                        <ChevronRight className="h-4 w-4 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100" />
                      </div>
                    </div>
                  </li>
                )
              })}
            </ul>
          )}
        </div>

        {chip === 'history' && hasMore && !loading && (
          <div className="border-t px-4 py-3 text-center">
            <Button variant="outline" size="sm" onClick={onLoadMore}>Load older orders</Button>
          </div>
        )}
      </Card>
    </div>
  )
}
